// frontend/src/features/agency/AgencyAvailableDriverList.js
// Phase 8.7C: Agency "Available" pool.
//
// Key principles:
// - Lane membership comes ONLY from: GET /agencies/me/drivers/available
// - driverSlice is an OPTIONAL entity cache (useful for modals), NOT lane truth.
// - Real-time invalidation is owned by services/socket.js (Option A/2),
//   so this component does NOT subscribe to socket events.
// - Reserve is NOT a driver.status mutation — it creates an assignment request.
//
// This file is intentionally "truth-following":
// - We never optimistically remove/move rows.
// - After any successful or failed action, we reload from the backend read model
//   so the UI always converges to the server truth.

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  upsertDrivers, // bulk upsert to keep entity cache fresh (optional)
  removeDriver,  // remove from entity cache on actual deletion
} from '../drivers/driverSlice';

// New “me” read model endpoint for available pool + reserve action
import {
  fetchAgencyAvailableDrivers,
  requestAgencyAssignment,
} from '../../services/driverService';

// Site selection (dropdown)
import { selectSelectedSiteId } from '../sites/sitesSlice';

// Legacy delete endpoint currently uses axios directly
import axios from 'axios';

import DriverEditModal from '../drivers/DriverEditModal';
import { Edit2, UserCheck, Trash2 } from 'lucide-react';

// We use agencyId only as a guard that auth state is ready / scoped.
// The endpoint is /me, so agency_id is implied by JWT.
import { selectAgencyIdForUser } from '../auth/authSlice';

// Used to trigger refresh (e.g., socket invalidation bumps this key globally)
import { selectQuery, selectStatsRefreshKey } from '../ui/filtersSlice';

function filterRowsByQuery(rows, q) {
  const needle = (q ?? '').trim().toLowerCase();
  if (!needle) return rows || [];

  return (rows || []).filter((row) => {
    const haystacks = [
      row?.name,
      row?.vehicle_registration_number,
      row?.assignment_status,
      row?.assignment_id != null ? String(row.assignment_id) : null,
      row?.ban_reason,
      row?.reason,
      row?.ban_scope,
      row?.ban_site_id != null ? String(row.ban_site_id) : null,
      row?.ban_id != null ? String(row.ban_id) : null,
    ];

    return haystacks.some((v) => String(v ?? '').toLowerCase().includes(needle));
  });
}


export default function AvailableDriverList() {
  const dispatch = useDispatch();

  // Auth + refresh guards
  const agencyId = useSelector(selectAgencyIdForUser);
  const statsKey = useSelector(selectStatsRefreshKey);
  const q = useSelector(selectQuery);


  // Selected site from dropdown (required for reserve)
  const selectedSiteId = useSelector(selectSelectedSiteId);

  // Local UI state
  const [loading, setLoading] = useState(true);

  // `rows` is the ONLY truth for what's rendered in this lane.
  const [rows, setRows] = useState([]);

  // For edit modal
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Prevent double-click / duplicate reserve requests per driver
  const [reserving, setReserving] = useState(() => new Set());

  /**
   * Reload the Available pool from the backend read model.
   * - rows drives membership (what we render).
   * - upsertDrivers(list) keeps the global entity cache warm (optional).
   *
   * IMPORTANT:
   * - If reload fails (e.g. auth 401), we surface it so it's not "mysterious".
   * - We avoid leaving stale membership on screen when a reload fails.
   */
  const reload = useCallback(async () => {
    try {
      const list = await fetchAgencyAvailableDrivers();
      const safeList = Array.isArray(list) ? list : [];

      setRows(safeList);

      // Optional cache warm (safe)
      dispatch(upsertDrivers(safeList));
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;

      console.error('[AvailableDriverList] reload failed:', {
        status,
        data,
        message: e?.message,
      });

      if (status === 401) {
        alert('Session expired. Please log in again.');
      } else {
        alert('Failed to refresh available drivers. Please try again.');
      }

      // Keep UI consistent: show empty list rather than stale membership.
      setRows([]);
    }
  }, [dispatch]);

  /**
   * Initial load:
   * Wait until agencyId exists (auth hydrated) then load.
   *
   * statsKey:
   * - When socket.js invalidates the agency lanes, it can bump this key,
   *   which triggers a reload here without direct socket subscriptions.
   */
  useEffect(() => {
    if (!agencyId) return;

    (async () => {
      try {
        await reload();
      } finally {
        setLoading(false);
      }
    })();
  }, [agencyId, statsKey, reload]);

  /**
   * Display sorting/transform.
   * NOTE: We keep it minimal here; membership is already determined by backend.
   */
  const filtered = useMemo(() => filterRowsByQuery(rows, q), [rows, q]);
  const sorted = useMemo(() => [...filtered], [filtered]);


  /**
   * Phase 8.7 — Request assignment (NOT a status mutation)
   *
   * This action does NOT change driver.status.
   * Instead, it creates an assignment in status = 'requested',
   * which makes the driver appear in the Reserved lane via
   * assignment-derived read models.
   *
   * Truth-following rules:
   * - No optimistic UI moves.
   * - On success: reload to converge to backend truth.
   * - On failure: log status/data and reload to snap back to backend truth.
   * - Prevent double-submit per driver via `reserving` set.
   */
  const handleReserve = async (id) => {
    const siteId = selectedSiteId ? Number(selectedSiteId) : null;

    if (!siteId) {
      alert('Select a site before requesting an assignment.');
      return;
    }

    // Per-driver in-flight lock
    if (reserving.has(id)) return;

    setReserving((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });

    try {
      // Diagnostic breadcrumb (useful when debugging intermittent 401/500)
      console.log('[AvailableDriverList] reserve click:', { driverId: id, siteId });

      // If your backend has ever thrown:
      // "null value in column assignment_type violates not-null constraint"
      // then pass assignment_type explicitly here (safe even if backend defaults).
      await requestAgencyAssignment({
        driverId: id,
        siteId,
        // assignment_type: 'SHIFT',
      });

      await reload();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;

      console.error('[AvailableDriverList] reserve failed:', {
        driverId: id,
        siteId,
        status,
        data,
        message: e?.message,
      });

      // Clear user-facing messaging by class of failure
      if (status === 401) {
        alert('Session expired. Please log in again.');
      } else if (status === 409) {
        // Common if backend rejects duplicates / open assignment exists
        alert(data?.message || 'This driver already has an open assignment.');
      } else if (status) {
        alert(data?.message || `Reserve failed (${status}). Please try again.`);
      } else {
        alert(`Reserve failed. ${e?.message || 'Unknown error'}`);
      }

      // Snap back to backend truth even after error
      await reload();
    } finally {
      setReserving((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  /**
   * Delete driver:
   * - Delete via backend
   * - Remove from redux entity cache
   * - Reload membership so list stays correct
   *
   * NOTE: This uses a legacy endpoint; keep as-is for now.
   * If you later move this into driverService, this component remains the same.
   */
  const handleDelete = async (id) => {
    try {
      await axios.delete(`/agencies/drivers/${id}`);
      dispatch(removeDriver(id));
      await reload();
    } catch (e) {
      const status = e?.response?.status;
      const data = e?.response?.data;

      console.error('[AvailableDriverList] delete failed:', {
        driverId: id,
        status,
        data,
        message: e?.message,
      });

      if (status === 401) {
        alert('Session expired. Please log in again.');
      } else {
        alert(data?.message || 'Delete failed. Please try again.');
      }

      // Attempt to converge to backend truth anyway
      await reload();
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading…</div>;
  }

return (
  <div className="my-6">
    {/* Header */}
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-2xl font-bold text-gray-800">
        Available Drivers
      </h3>
    </div>

    {q?.trim() && sorted.length === 0 && (
      <div className="text-sm text-gray-500 py-3">
        No matches for “{q.trim()}”.
      </div>
    )}


    {/* Table */}
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            {['Name', 'Vehicle Reg.', 'Actions'].map((col) => (
              <th
                key={col}
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="divide-y divide-gray-200">
          {sorted.map((row) => (
            <tr key={row.id} className="hover:bg-gray-50">
              
              <td className="px-6 py-4 whitespace-nowrap">{row.name}</td>
              <td className="px-6 py-4 whitespace-nowrap">{row.vehicle_registration_number}</td>

              <td className="px-6 py-4">
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setSelectedDriver(row)}
                    className="p-1 hover:bg-gray-100 rounded"
                    title="Edit"
                  >
                    <Edit2 size={16} />
                  </button>

                  <button
                    onClick={() => handleReserve(row.id)}
                    disabled={reserving.has(row.id)}
                    className={`p-1 hover:bg-gray-100 rounded text-yellow-600 ${
                      reserving.has(row.id)
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    title={
                      reserving.has(row.id)
                        ? 'Reserving…'
                        : 'Reserve'
                    }
                  >
                    <UserCheck size={16} />
                  </button>

                  <button
                    onClick={() => handleDelete(row.id)}
                    className="p-1 hover:bg-gray-100 rounded text-red-600"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    {/* Edit modal */}
    {selectedDriver && (
      <DriverEditModal
        driver={selectedDriver}
        onClose={() => setSelectedDriver(null)}
        onUpdate={async () => {
          setSelectedDriver(null);
          await reload();
        }}
      />
    )}
  </div>
);
}